// lib/demoProblemGenerator.ts
import { DemoCompany } from './demoCompanyService';
import { SubmissionData } from './submissionService';

export interface GeneratedDemoProblem {
  id: string;
  title: string;
  data: SubmissionData;
  createdAt: string;
}

// Industry-specific problem templates
const PROBLEM_TEMPLATES = {
  'fmcg': [
    {
      title: 'Bottling Line Conveyor Misalignment',
      category: 'downtime',
      description: 'The primary bottling line conveyor belt experiences chronic misalignment every 45-60 minutes of operation, causing product jams and requiring manual intervention. The issue has worsened over 18 months, with misalignment frequency increasing from 2-3 times per shift to 5-6 times per shift. Each incident causes 12-18 minutes of downtime, resulting in significant production losses and overtime labor costs.',
      rootCause: 'Gradual tension drift in the belt system due to worn tensioning springs, detected only after product jams occur. The current inspection protocol only catches issues after failure.',
      equipment: 'KHS Bottling Line Conveyor System, Siemens S7-1200 PLC',
      evidence: ['Machine maintenance and downtime logs', 'Production yield and waste records', 'Labour time and overtime records']
    },
    {
      title: 'Packaging Machine Frequent Jams',
      category: 'quality_control',
      description: 'Packaging line stops 5-6 times daily due to misaligned sealing mechanism. Each jam takes 15-20 minutes to clear. The issue started 6 months ago after a major maintenance overhaul. The problem is intermittent and difficult to diagnose, occurring more frequently during high-speed production runs.',
      rootCause: 'Misaligned sealing mechanism after maintenance, compounded by worn guide rails and inconsistent film tension',
      equipment: 'Tetra Pak Packaging Line, Servo-driven sealing heads',
      evidence: ['Quality control reports', 'Maintenance logs', 'Overtime records', 'Production efficiency reports']
    },
    {
      title: 'Cooling System Efficiency Loss',
      category: 'energy_loss',
      description: 'Cooling towers operating at 70% efficiency, causing increased energy costs and inconsistent temperature control during peak production hours. Energy consumption has increased 30% year-over-year. The system struggles to maintain set points during afternoon heat, affecting product quality.',
      rootCause: 'Biofilm buildup in cooling towers and fouled heat exchangers, combined with inefficient pump scheduling and lack of variable speed drives',
      equipment: 'Evapco Cooling Towers, Plate heat exchangers, Centrifugal pumps',
      evidence: ['Energy bills (last 12 months)', 'Temperature logs', 'Maintenance records', 'Efficiency test reports']
    }
  ],
  'agro_processing': [
    {
      title: 'Cold Chain Temperature Breaches',
      category: 'cold_chain',
      description: 'Temperature fluctuations during transport of fresh milk products causing spoilage. Data from temperature loggers shows 12% of deliveries experience breaches above 6°C, leading to product rejection. The problem is worse on longer routes and during hot weather, with no real-time alerts to drivers.',
      rootCause: 'Refrigeration unit calibration drift, inadequate insulation, and lack of real-time monitoring during transit',
      equipment: 'Thermo King refrigeration units, Temperature data loggers, Insulated trucks',
      evidence: ['Temperature logger data', 'Spoilage records', 'Customer rejection reports', 'Transport logs']
    },
    {
      title: 'Moisture Meter Calibration Drift',
      category: 'quality_control',
      description: 'Moisture meters used for quality control drift out of calibration within 2-3 weeks, requiring frequent recalibration. This causes inconsistent tea grading and occasional customer complaints. The drift accelerates during humid weather, suggesting environmental sensitivity.',
      rootCause: 'Sensor degradation in humid environment and lack of automated calibration verification',
      equipment: 'Laboratory moisture analyzers, In-line NIR sensors',
      evidence: ['Calibration records', 'Quality grade data', 'Customer feedback logs', 'Environmental monitoring data']
    },
    {
      title: 'Tea Dryer Energy Overconsumption',
      category: 'energy_loss',
      description: 'Tea dryers consume 30% more energy than 5 years ago. Maintenance says equipment is fine, but fuel bills keep rising. Suspect inefficient combustion or heat loss. The dryers also show inconsistent temperature profiles, affecting product quality.',
      rootCause: 'Poor combustion efficiency due to burner fouling, heat exchanger scaling, and inadequate insulation',
      equipment: 'Fluid bed dryers, Heat exchangers, Burners',
      evidence: ['Fuel bills (last 12 months)', 'Production logs', 'Efficiency test reports', 'Temperature profiles']
    }
  ],
  'manufacturing': [
    {
      title: 'CNC Machine Calibration Drift',
      category: 'quality_control',
      description: 'CNC machines experience calibration drift over 2-3 week periods, causing parts to be out of tolerance. Current practice is reactive recalibration after quality failures. The drift correlates with ambient temperature changes and machine warm-up cycles.',
      rootCause: 'Thermal expansion effects on ball screws and lack of real-time compensation algorithms',
      equipment: '5-axis CNC machining centers, Renishaw probes, Ball screws',
      evidence: ['Quality inspection reports', 'Machine maintenance logs', 'Calibration records', 'Temperature logs']
    },
    {
      title: 'Predictive Maintenance Gap',
      category: 'maintenance',
      description: 'Critical pumps and motors fail without warning, causing unplanned downtime. Vibration analysis is done manually monthly, missing early failure signs. Recent failures caused 40+ hours of downtime and KES 2.5M in emergency repairs.',
      rootCause: 'Lack of continuous vibration monitoring and analysis, no automated alerting system',
      equipment: 'Centrifugal pumps, Electric motors, Fans, Vibration sensors',
      evidence: ['Downtime logs', 'Maintenance records', 'Vibration analysis reports', 'Repair cost records']
    },
    {
      title: 'Conveyor Belt Wear Monitoring',
      category: 'maintenance',
      description: 'Raw material conveyors experience unpredictable belt wear. Current practice is reactive replacement after failure, causing 8-12 hours downtime per incident. Belt failures have increased 40% year-over-year as the system ages.',
      rootCause: 'No predictive monitoring system for belt condition, misalignment issues accelerating wear',
      equipment: 'Overland conveyors, Belt scales, Drive pulleys',
      evidence: ['Belt replacement records', 'Downtime logs', 'Maintenance cost reports', 'Inspection photos']
    }
  ],
  'default': [
    {
      title: 'Operational Efficiency Loss',
      category: 'process_inefficiency',
      description: 'Production line efficiency has declined 15% over the past year. Multiple small issues contribute to overall performance degradation, including minor stoppages, slow changeovers, and inconsistent operator performance.',
      rootCause: 'Combination of equipment wear, operator variability, lack of real-time performance data, and inadequate standard operating procedures',
      equipment: 'Production line equipment, HMI systems',
      evidence: ['Production reports', 'Efficiency calculations', 'Operator logs', 'Changeover time records']
    },
    {
      title: 'Quality Control Failure',
      category: 'quality_control',
      description: 'Intermittent quality issues lead to 5% rework rate. Root cause is difficult to identify due to lack of real-time quality data. Issues appear randomly across different products and shifts.',
      rootCause: 'Undetected process variations and lack of inline quality monitoring, combined with inadequate statistical process control',
      equipment: 'Quality testing equipment, CMM machines',
      evidence: ['Quality reports', 'Rework records', 'Customer feedback', 'Shift logs']
    }
  ]
};

// Financial impact ranges by industry (KES)
const FINANCIAL_RANGES = {
  'fmcg': { min: 5000000, max: 25000000 },
  'agro_processing': { min: 3000000, max: 20000000 },
  'manufacturing': { min: 8000000, max: 35000000 },
  'default': { min: 4000000, max: 15000000 }
};

// Frequency options
const FREQUENCY_OPTIONS = [
  'Continuous (happening right now, every day)',
  'Recurring daily',
  'Recurring weekly',
  'Recurring monthly',
  'Seasonal',
  'Episodic but unpredictable'
];

// Scope options
const SCOPE_OPTIONS = [
  'Affects a single machine or asset',
  'Affects a production line',
  'Affects an entire department',
  'Affects the whole facility',
  'Affects multiple facilities in our network'
];

// Pilot likelihood options
const PILOT_OPTIONS = [
  'Very likely — we would pilot immediately',
  'Likely — subject to internal approval',
  'Possible — depends on the solution',
  'Unlikely — submitting for awareness only',
  'Unsure at this point'
];

// Industry prevalence options
const PREVALENCE_OPTIONS = [
  'I believe it is unique to our facility',
  'I have heard of similar problems at other facilities in our sector',
  'I know of multiple competitors / peers who face the same problem',
  'This is widely understood to be an industry-wide problem',
  'I do not know'
];

// Approval authority options
const APPROVAL_OPTIONS = [
  'Operations Manager (I am the approver)',
  'Plant / Factory Manager',
  'Chief Operations Officer',
  'Chief Executive Officer / MD',
  'Board of Directors',
  'Parent company / international HQ approval required'
];

// Commercial model options
const COMMERCIAL_MODEL_OPTIONS = [
  'One-time purchase',
  'Annual subscription',
  'Pay-from-savings',
  'Open to discussion'
];

// Connectivity options
const CONNECTIVITY_OPTIONS = [
  'Reliable fibre broadband',
  'Mobile data (4G/3G) only',
  'Limited / intermittent connectivity',
  'No internet connectivity at the site',
  'Secure corporate network — external devices must be approved'
];

export const demoProblemGenerator = {
  /**
   * Generate a random realistic problem for a demo company
   */
  generateProblem: (company: DemoCompany): SubmissionData => {
    // Get templates for this industry or use default
    const industryKey = company.industry as keyof typeof PROBLEM_TEMPLATES;
    const templates = PROBLEM_TEMPLATES[industryKey] || PROBLEM_TEMPLATES.default;
    const template = templates[Math.floor(Math.random() * templates.length)];
    
    // Get financial range for this industry
    const range = FINANCIAL_RANGES[industryKey] || FINANCIAL_RANGES.default;
    
    // Generate realistic financial figures (KES) with some variation
    const totalAnnualCost = Math.floor(Math.random() * (range.max - range.min) + range.min);
    
    // Distribute total across categories (roughly)
    const costEnergy = Math.floor(totalAnnualCost * (0.1 + Math.random() * 0.1));
    const costMaterial = Math.floor(totalAnnualCost * (0.15 + Math.random() * 0.1));
    const costDowntime = Math.floor(totalAnnualCost * (0.25 + Math.random() * 0.1));
    const costLabour = Math.floor(totalAnnualCost * (0.15 + Math.random() * 0.1));
    const costCompliance = Math.floor(totalAnnualCost * (0.05 + Math.random() * 0.05));
    const costOther = totalAnnualCost - (costEnergy + costMaterial + costDowntime + costLabour + costCompliance);
    
    // Generate market estimates based on industry
    const estimatedKenya = Math.floor(Math.random() * 40 + 10);
    const estimatedEastAfrica = Math.floor(estimatedKenya * (2.5 + Math.random()));
    
    // Random urgency with weighted probability
    const urgencyRand = Math.random();
    const urgency = urgencyRand < 0.5 ? 'high' : urgencyRand < 0.8 ? 'medium' : 'low';
    
    // Random confidence level (3-5 usually)
    const confidenceLevel = (Math.floor(Math.random() * 3 + 3)).toString();
    
    // Random selection from option arrays
    const frequency = FREQUENCY_OPTIONS[Math.floor(Math.random() * FREQUENCY_OPTIONS.length)];
    const scope = SCOPE_OPTIONS[Math.floor(Math.random() * SCOPE_OPTIONS.length)];
    const pilotLikelihood = PILOT_OPTIONS[Math.floor(Math.random() * PILOT_OPTIONS.length)];
    const industryPrevalence = PREVALENCE_OPTIONS[Math.floor(Math.random() * PREVALENCE_OPTIONS.length)];
    const approvalAuthority = APPROVAL_OPTIONS[Math.floor(Math.random() * APPROVAL_OPTIONS.length)];
    const commercialModel = COMMERCIAL_MODEL_OPTIONS[Math.floor(Math.random() * COMMERCIAL_MODEL_OPTIONS.length)];
    const connectivity = company.connectivity || CONNECTIVITY_OPTIONS[Math.floor(Math.random() * CONNECTIVITY_OPTIONS.length)];
    
    // Generate seasonal months if frequency is Seasonal
    const seasonalMonths = frequency === 'Seasonal' ? 
      ['December-February', 'June-August', 'March-May', 'September-November'][Math.floor(Math.random() * 4)] : 
      undefined;
    
    // First appeared (months ago)
    const firstAppearedMonths = Math.floor(Math.random() * 24 + 6);
    
    // Generate evidence list (add some random additional evidence)
    const allEvidence = [
      'Electricity bills (last 12 months)',
      'Water / utilities bills (last 12 months)',
      'Machine maintenance and downtime logs',
      'Production yield and waste records',
      'Inventory shrinkage or spoilage records',
      'Quality control and rework reports',
      'Labour time and overtime records',
      'ERP or MES system data exports',
      'Previous consultant or audit reports',
      'Insurance claims related to the problem',
      'Photographs or video of the problem',
      'Equipment specifications and manuals'
    ];
    
    // Start with template evidence, add 1-2 more
    const evidence = [...template.evidence];
    while (evidence.length < 4 && evidence.length < allEvidence.length) {
      const randomEvidence = allEvidence[Math.floor(Math.random() * allEvidence.length)];
      if (!evidence.includes(randomEvidence)) {
        evidence.push(randomEvidence);
      }
    }
    
    // Generate sought help options
    const helpOptions = ['consultant', 'equipment', 'internal', 'none'];
    const soughtHelpCount = Math.floor(Math.random() * 3);
    const soughtHelp: string[] = [];
    for (let i = 0; i < soughtHelpCount; i++) {
      const help = helpOptions[Math.floor(Math.random() * helpOptions.length)];
      if (!soughtHelp.includes(help)) {
        soughtHelp.push(help);
      }
    }
    
    // Generate pilot access
    const accessOptions = ['area', 'data', 'liaison', 'install'];
    const pilotAccessCount = Math.floor(Math.random() * 3) + 1;
    const pilotAccess: string[] = [];
    for (let i = 0; i < pilotAccessCount; i++) {
      const access = accessOptions[Math.floor(Math.random() * accessOptions.length)];
      if (!pilotAccess.includes(access)) {
        pilotAccess.push(access);
      }
    }
    
    // Generate monitoring systems
    const systemOptions = ['SCADA', 'ERP', 'MES', 'Basic PLCs', 'Manual records', 'IoT sensors', 'Energy management'];
    const systemCount = Math.floor(Math.random() * 4) + 1;
    const monitoringSystems: string[] = [];
    for (let i = 0; i < systemCount; i++) {
      const system = systemOptions[Math.floor(Math.random() * systemOptions.length)];
      if (!monitoringSystems.includes(system)) {
        monitoringSystems.push(system);
      }
    }
    
    // Generate certifications
    const certOptions = ['ISO 9001', 'ISO 14001', 'ISO 50001', 'ISO 22000', 'HACCP', 'KEBS', 'Fair Trade'];
    const certCount = Math.floor(Math.random() * 3) + 1;
    const certifications: string[] = [];
    for (let i = 0; i < certCount; i++) {
      const cert = certOptions[Math.floor(Math.random() * certOptions.length)];
      if (!certifications.includes(cert)) {
        certifications.push(cert);
      }
    }
    
    // Self-assessment ratings (1-5)
    const severityRating = (Math.floor(Math.random() * 2 + 4)).toString(); // 4-5
    const urgencyRating = urgency === 'high' ? '5' : urgency === 'medium' ? '4' : '3';
    const prevalenceRating = (Math.floor(Math.random() * 2 + 3)).toString(); // 3-4
    const dataAvailabilityRating = (Math.floor(Math.random() * 2 + 3)).toString(); // 3-4
    const techOpennessRating = (Math.floor(Math.random() * 2 + 4)).toString(); // 4-5
    const managementSupportRating = (Math.floor(Math.random() * 2 + 3)).toString(); // 3-4
    const decisionSpeedRating = (Math.floor(Math.random() * 2 + 3)).toString(); // 3-4
    
    // Determine if regulatory related
    const regulatoryRand = Math.random();
    const regulatoryRelated = regulatoryRand < 0.4 ? 'yes-penalties' : 
                              regulatoryRand < 0.6 ? 'yes-anticipated' : 
                              regulatoryRand < 0.9 ? 'no' : 'unsure';
    
    // Generate submission data
    return {
      // Contact Information - Use company's actual contact
      contactName: company.contactName,
      contactTitle: company.contactTitle,
      contactEmail: company.email,
      contactPhone: company.phone,
      contactDepartment: 'Operations',
      yearsInRole: (Math.floor(Math.random() * 10 + 2)).toString(),
      decisionMaker: Math.random() > 0.5 ? 'I am the decision-maker' : 'I report to the decision-maker',
      decisionMakerDetails: Math.random() > 0.7 ? 'Reports to Operations Director' : undefined,
      
      // Secondary contact (optional, 50% chance)
      secondaryName: Math.random() > 0.5 ? `John ${company.contactName.split(' ')[1] || 'Kamau'}` : undefined,
      secondaryTitle: Math.random() > 0.5 ? 'Maintenance Supervisor' : undefined,
      secondaryEmail: Math.random() > 0.5 ? `john@${company.email.split('@')[1]}` : undefined,
      secondaryPhone: Math.random() > 0.5 ? company.phone.replace(/\d{3}$/, '789') : undefined,
      
      // Problem Details
      problemTitle: template.title,
      problemCategory: template.category,
      otherCategory: template.category === 'other' ? 'Custom problem category' : undefined,
      plainDescription: template.description,
      rootCause: template.rootCause,
      firstAppeared: `${firstAppearedMonths} months ago`,
      frequency: frequency,
      seasonalMonths: seasonalMonths,
      scope: scope,
      previousAttempts: 'Previous attempts included reactive maintenance, manual adjustments, and consultant recommendations. None have addressed the root cause permanently.',
      
      // Financial Impact
      costEnergy: costEnergy,
      costMaterial: costMaterial,
      costDowntime: costDowntime,
      costLabour: costLabour,
      costCompliance: costCompliance,
      costOther: costOther,
      costOtherDescription: costOther > 0 ? 'Miscellaneous operational costs' : undefined,
      totalAnnualCost: totalAnnualCost,
      evidence: evidence,
      otherEvidence: 'Additional documentation available upon request',
      confidenceLevel: confidenceLevel,
      estimateDerivation: 'Calculated from historical data, operational records, and industry benchmarks',
      
      // Scalability
      industryPrevalence: industryPrevalence,
      estimatedKenya: estimatedKenya,
      estimatedEastAfrica: estimatedEastAfrica,
      affectedIndustries: company.industrySectors?.join(', ') || company.industry.replace('_', ' '),
      existingSolutions: 'Some commercial solutions exist but are expensive (KES 2-5M) and don\'t fully address the root cause',
      soughtHelp: soughtHelp,
      
      // Pilot Readiness
      pilotLikelihood: pilotLikelihood,
      approvalAuthority: approvalAuthority,
      approvalTimeline: `${Math.floor(Math.random() * 4 + 2)} weeks`,
      pilotDuration: `${Math.floor(Math.random() * 60 + 30)} days`,
      successCriteria: `Reduce ${template.category} incidents by ${Math.floor(Math.random() * 30 + 30)}% and improve MTBF by ${Math.floor(Math.random() * 50 + 50)}%`,
      budgetRange: ['Below KES 1M', 'KES 1M - 5M', 'KES 5M - 10M', 'KES 10M - 25M', 'Above KES 25M'][Math.floor(Math.random() * 3 + 1)],
      commercialModel: commercialModel,
      pilotAccess: pilotAccess,
      
      // Technical Environment
      facilityAge: company.facilityAge || (Math.floor(Math.random() * 40 + 10)).toString(),
      facilitySize: company.facilitySize || (Math.floor(Math.random() * 150000 + 50000)).toString(),
      operatingHours: company.operatingHours || (Math.random() > 0.5 ? '24/7' : '2 shifts, 16 hrs/day'),
      operatingDays: company.operatingDays || (Math.floor(Math.random() * 50 + 300)).toString(),
      monitoringSystems: company.monitoringSystems || monitoringSystems,
      connectivity: connectivity,
      primaryEquipment: template.equipment,
      equipmentBrand: company.industry === 'fmcg' ? 'KHS / Tetra Pak / Siemens' : 
                      company.industry === 'agro_processing' ? 'GEA / Alfa Laval / Thermo King' : 
                      'Siemens / ABB / Fanuc / FLSmidth',
      equipmentAge: (Math.floor(Math.random() * 20 + 5)).toString(),
      maintenanceSpend: Math.floor(totalAnnualCost * (0.2 + Math.random() * 0.2)),
      lastOverhaul: `${Math.floor(Math.random() * 5 + 1)} years ago`,
      solutionType: ['IoT sensors', 'Predictive maintenance', 'Automated controls', 'Monitoring software'].slice(0, Math.floor(Math.random() * 3 + 1)),
      technicalConstraints: 'Cannot interrupt production during peak hours. Limited physical space for additional equipment. Must comply with safety regulations.',
      
      // Regulatory Context
      regulatoryRelated: regulatoryRelated,
      regulatoryBodies: regulatoryRelated.includes('yes') ? 'KEBS, NEMA, Local county government' : undefined,
      certifications: company.certifications || certifications,
      upcomingDeadlines: regulatoryRelated.includes('yes') ? 'Annual compliance audit in 3 months' : undefined,
      
      // Self-Assessment
      severityRating: severityRating,
      urgencyRating: urgencyRating,
      prevalenceRating: prevalenceRating,
      dataAvailabilityRating: dataAvailabilityRating,
      techOpennessRating: techOpennessRating,
      managementSupportRating: managementSupportRating,
      decisionSpeedRating: decisionSpeedRating,
      additionalContext: 'We have been tracking this issue for over a year and have collected substantial data. Management is supportive of finding a permanent solution.',
      
      // Consent - All true for demo
      consentStorage: true,
      consentContact: true,
      consentAnonymised: true,
      consentUpdates: true,
      consentSquad: true,
      dataSharingLevel: 'identified-after-nda',
      declarationAgreed: true,
      
      // No files initially
      documents: []
    };
  },
  
  /**
   * Generate multiple problems at once
   */
  generateMultipleProblems: (company: DemoCompany, count: number = 3): SubmissionData[] => {
    const problems: SubmissionData[] = [];
    // Use a set to track titles and avoid duplicates
    const usedTitles = new Set<string>();
    
    for (let i = 0; i < count; i++) {
      let problem: SubmissionData;
      let attempts = 0;
      
      // Try to generate a problem with a unique title
      do {
        problem = demoProblemGenerator.generateProblem(company);
        attempts++;
        // If we've tried too many times, just accept whatever we have
        if (attempts > 10) break;
      } while (usedTitles.has(problem.problemTitle));
      
      usedTitles.add(problem.problemTitle);
      problems.push(problem);
    }
    
    return problems;
  },
  
  /**
   * Generate a problem with specific parameters
   */
  generateCustomProblem: (
    company: DemoCompany,
    options: {
      category?: string;
      urgency?: 'high' | 'medium' | 'low';
      minImpact?: number;
      maxImpact?: number;
    }
  ): SubmissionData => {
    const problem = demoProblemGenerator.generateProblem(company);
    
    // Override with custom options
    if (options.category) {
      problem.problemCategory = options.category;
    }
    
    if (options.urgency) {
      problem.urgencyRating = options.urgency === 'high' ? '5' : options.urgency === 'medium' ? '4' : '3';
    }
    
    if (options.minImpact && options.maxImpact) {
      const newTotal = Math.floor(Math.random() * (options.maxImpact - options.minImpact) + options.minImpact);
      // Adjust components proportionally
      const ratio = newTotal / problem.totalAnnualCost;
      problem.costEnergy = Math.floor((problem.costEnergy || 0) * ratio);
      problem.costMaterial = Math.floor((problem.costMaterial || 0) * ratio);
      problem.costDowntime = Math.floor((problem.costDowntime || 0) * ratio);
      problem.costLabour = Math.floor((problem.costLabour || 0) * ratio);
      problem.costCompliance = Math.floor((problem.costCompliance || 0) * ratio);
      problem.totalAnnualCost = newTotal;
      problem.estimatedImpactValue = newTotal;
    }
    
    return problem;
  }
};